<script lang="ts" setup>
  import { onMounted, ref, type Ref, watch } from 'vue'
  import api from '@/services/api'
  import InputText from 'primevue/inputtext'
  import InputNumber, { InputNumberInputEvent } from 'primevue/inputnumber'
  import Button from 'primevue/button'
  import { useBreakpointsStore, useProjectStore } from '@/store'
  import { storeToRefs } from 'pinia'
  import { useToast } from 'primevue/usetoast'
  import PermissionRole from '@/components/PermissionRole/PermissionComponent.vue'
  import { useRoute } from 'vue-router'
  import { i18n } from '@/i18n'
  import { captureException } from '@sentry/vue'
  // import SkeletonLaborBill from '@/components/SkeletonLaborBill.vue'
  import { delay } from '@/utils/delay'

  interface IInfo {
    companyLogoDarkUrl: string
    companyLogoUrl: string
    customerAddressLine1: string
    customerAddressLine2: string
    customerName: string
    customerNumber: null
    detailAreaOfWork: string
    detailDumpster: string
    detailGutterProblems: string
    detailIceDamArea: string
    detailLeakingArea: string
    detailPreferredTimeline: string
    detailRoofAccess: string
    detailRoofingProblems: string
    detailSatelliteDish: string
    items: string
    salespersonName: null
    themeColor: string
    timeZone: string
    totalLaborCost: string
    typeVersion: null
  }

  interface IResponse {
    info: IInfo
    createdTime: string
    estimateId: number
    name: string
    pdfUrl: string
    type: string
    workflows: number[]
  }

  interface Iitem {
    description: string
    laborCost: number | undefined
    quantity: number | undefined
    laborCostTotal: number | undefined
  }

  const validateValuesObj = (obj: { [key: string]: any }) => {
    for (const key in obj) {
      if (obj[key] === 'null') obj[key] = 'None'
      obj[key] = obj[key] ?? 'None'
    }
    return obj
  }
  const toast = useToast()
  const breakpointsStore = useBreakpointsStore()
  const projectStore = useProjectStore()
  const { project } = storeToRefs(projectStore)
  const { md } = storeToRefs(breakpointsStore)
  const formLoading = ref(false)
  const currentDocument = ref<IResponse>({} as IResponse)
  const loadingLaborbill = ref(false)
  const items = ref<Iitem[]>([])
  const totalLaborCost = ref(0)
  const route = useRoute()
  const projectId = ref(`${route.params.id}`)
  const documentType: Ref<string> = ref('LaborBill')
  const { t } = i18n.global
  async function fetchLaborBill() {
    try {
      loadingLaborbill.value = true
      const {
        data: { data: response },
      } = await api.get(`/Api/Project/${projectId.value}/Documents/Latest/LaborBill`)
      await delay(2000)
      currentDocument.value = response
      items.value = response.info?.items ? JSON.parse(response.info?.items) : [{ description: null, laborCost: null, quantity: null, laborCostTotal: null }]
      totalLaborCost.value = Number(response?.info?.totalLaborCost ?? 0)
    } catch (err) {
      captureException(err)
      console.log('err', err)
    } finally {
      loadingLaborbill.value = false
    }
  }

  async function submitDocument() {
    try {
      formLoading.value = true
      console.log('currentDocument', currentDocument.value)

      const payload = {
        estimateId: currentDocument.value.estimateId,
        createdTime: new Date(),
        type: documentType.value,
        workflows: currentDocument.value.workflows,
        name: currentDocument.value.name,
        data: { ...validateValuesObj(currentDocument.value.info), items: JSON.stringify(items.value), totalLaborCost: String(totalLaborCost.value) },
      }

      await api.post(`/Api/Project/${projectId.value}/Documents/Update`, payload)

      await fetchLaborBill()

      toast.add({ severity: 'success', summary: t('projectLaborBill.alertSuccess'), detail: t('projectLaborBill.alertSuccessMessage'), life: 3000 })
    } catch (err) {
      toast.add({ severity: 'error', summary: t('projectLaborBill.alertError'), detail: t('projectLaborBill.alertErrorMessage'), life: 3000 })
    } finally {
      formLoading.value = false
    }
  }

  function handleLaborCostUpdate(event: InputNumberInputEvent, index: number) {
    const laborCost = Number(event.value ?? 0)
    const quantity = items.value[index].quantity ?? 0
    items.value[index].laborCostTotal = laborCost * quantity
  }

  function handleQuantityUpdate(event: InputNumberInputEvent, index: number) {
    const laborCost = items.value[index].laborCost
    const quantity = Number(event.value ?? 0)
    items.value[index].laborCostTotal = quantity * (laborCost ?? 0)
  }

  function calculateTotalLaborCost() {
    let tlc = 0
    for (const item of items.value) {
      const lc = item.laborCost ?? 0
      const q = item.quantity ?? 0
      const lct = lc * q
      tlc += lct
    }
    totalLaborCost.value = tlc
  }

  function addItem() {
    items.value.push({ description: '', laborCost: undefined, quantity: undefined, laborCostTotal: undefined })
  }

  function deleteItem(index: number) {
    items.value.splice(index, 1)
  }

  watch(
    items,
    () => {
      calculateTotalLaborCost()
    },
    { deep: true }
  )

  onMounted(async () => {
    await fetchLaborBill()
  })
</script>

<template>
  <div class="col-12">
    <div class="flex align-items-center justify-content-between title-tab">
      <div class="flex text-xl align-items-center">
        <div class="flex pr-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-clipboard-money" viewBox="0 0 16 16">
            <path
              d="M4 1.5H3C2.46957 1.5 1.96086 1.71071 1.58579 2.08579C1.21071 2.46086 1 2.96957 1 3.5V14C1 14.5304 1.21071 15.0391 1.58579 15.4142C1.96086 15.7893 2.46957 16 3 16H13C13.5304 16 14.0391 15.7893 14.4142 15.4142C14.7893 15.0391 15 14.5304 15 14V3.5C15 2.96957 14.7893 2.46086 14.4142 2.08579C14.0391 1.71071 13.5304 1.5 13 1.5H12V2.5H13C13.2652 2.5 13.5196 2.60536 13.7071 2.79289C13.8946 2.98043 14 3.23478 14 3.5V14C14 14.2652 13.8946 14.5196 13.7071 14.7071C13.5196 14.8946 13.2652 15 13 15H3C2.73478 15 2.48043 14.8946 2.29289 14.7071C2.10536 14.5196 2 14.2652 2 14V3.5C2 3.23478 2.10536 2.98043 2.29289 2.79289C2.48043 2.60536 2.73478 2.5 3 2.5H4V1.5Z"
            />
            <path
              d="M9.5 1C9.63261 1 9.75979 1.05268 9.85355 1.14645C9.94732 1.24021 10 1.36739 10 1.5V2.5C10 2.63261 9.94732 2.75979 9.85355 2.85355C9.75979 2.94732 9.63261 3 9.5 3H6.5C6.36739 3 6.24021 2.94732 6.14645 2.85355C6.05268 2.75979 6 2.63261 6 2.5V1.5C6 1.36739 6.05268 1.24021 6.14645 1.14645C6.24021 1.05268 6.36739 1 6.5 1H9.5ZM6.5 0C6.10218 0 5.72064 0.158035 5.43934 0.43934C5.15804 0.720644 5 1.10218 5 1.5V2.5C5 2.89782 5.15804 3.27936 5.43934 3.56066C5.72064 3.84196 6.10218 4 6.5 4H9.5C9.89782 4 10.2794 3.84196 10.5607 3.56066C10.842 3.27936 11 2.89782 11 2.5V1.5C11 1.10218 10.842 0.720644 10.5607 0.43934C10.2794 0.158035 9.89782 0 9.5 0L6.5 0Z"
            />
            <path
              d="M7.6441 11.3376L7.64686 9.69029C7.64687 9.68313 7.64434 9.67619 7.6397 9.67061C7.63505 9.66504 7.62857 9.66117 7.62132 9.65964C6.8732 9.50311 6.12025 9.16272 5.83384 8.42599C5.35694 7.19499 6.44255 6.31704 7.60338 6.22245C7.63053 6.22023 7.6441 6.20602 7.6441 6.17982V5.89006C7.6441 5.84432 7.66756 5.82145 7.71449 5.82145L8.34805 5.81812C8.37566 5.81812 8.389 5.83144 8.38808 5.85808L8.37842 6.16516C8.37802 6.17999 8.38352 6.1944 8.39379 6.20543C8.40406 6.21645 8.41832 6.22326 8.43363 6.22445C9.33497 6.29772 10.0148 6.7347 10.2653 7.58734C10.2669 7.59274 10.2673 7.59842 10.2665 7.60399C10.2656 7.60956 10.2635 7.61488 10.2603 7.61958C10.257 7.62427 10.2528 7.62823 10.2478 7.63118C10.2428 7.63412 10.2373 7.63598 10.2315 7.63663L9.0244 7.77252C9.01645 7.77328 9.00846 7.7713 9.00179 7.76693C8.99512 7.76256 8.99019 7.75606 8.98782 7.74854C8.90132 7.45855 8.71498 7.25361 8.4288 7.13371C8.42399 7.13173 8.41875 7.13094 8.41354 7.13139C8.40834 7.13184 8.40333 7.13352 8.39896 7.13629C8.39459 7.13906 8.39099 7.14282 8.38849 7.14725C8.38599 7.15168 8.38467 7.15664 8.38463 7.16168L8.38256 8.66845C8.38261 8.68209 8.38754 8.69527 8.39648 8.70573C8.40542 8.71618 8.41783 8.72326 8.43156 8.72574C9.2977 8.87895 10.3046 9.22533 10.4516 10.2119C10.6352 11.4402 9.59653 12.2169 8.43432 12.3161C8.41914 12.3176 8.40508 12.3245 8.39487 12.3353C8.38467 12.3461 8.37905 12.3602 8.37911 12.3748L8.38532 13.0609C8.38541 13.0647 8.38471 13.0685 8.38326 13.072C8.38181 13.0756 8.37964 13.0788 8.37688 13.0816C8.37411 13.0843 8.37081 13.0865 8.36716 13.0879C8.36352 13.0894 8.3596 13.0902 8.35564 13.0902L7.69034 13.0908C7.67881 13.0908 7.66775 13.0864 7.65959 13.0786C7.65144 13.0707 7.64686 13.06 7.64686 13.0489L7.64479 12.3868C7.64479 12.3534 7.62776 12.3355 7.59372 12.3328C6.48166 12.2382 5.79335 11.6733 5.52879 10.6382C5.52707 10.6314 5.52688 10.6244 5.52825 10.6176C5.52961 10.6108 5.53249 10.6043 5.53669 10.5987C5.54089 10.5931 5.54631 10.5884 5.55257 10.5849C5.55883 10.5815 5.56579 10.5795 5.57296 10.5789L6.8035 10.4743C6.81786 10.473 6.83224 10.4765 6.8441 10.4843C6.85596 10.4921 6.86455 10.5037 6.86837 10.5169C6.98478 10.9042 7.23346 11.1837 7.61442 11.3556C7.61758 11.3571 7.62106 11.3577 7.62456 11.3575C7.62805 11.3573 7.63144 11.3563 7.6344 11.3545C7.63737 11.3527 7.63981 11.3502 7.64151 11.3473C7.6432 11.3443 7.64409 11.341 7.6441 11.3376ZM7.24105 7.31889C7.17529 7.36435 7.12025 7.42269 7.07957 7.49009C7.03889 7.55749 7.01348 7.6324 7.00502 7.7099C6.96085 8.10491 7.25347 8.37603 7.6089 8.49793C7.61286 8.4992 7.61707 8.49957 7.6212 8.49901C7.62532 8.49844 7.62923 8.49696 7.63261 8.49468C7.63598 8.4924 7.63872 8.48939 7.6406 8.4859C7.64248 8.48241 7.64344 8.47854 7.64341 8.47461L7.64686 7.29291C7.64732 7.12283 7.576 7.0873 7.43291 7.18633L7.24105 7.31889ZM8.37911 9.89146L8.38739 11.3862C8.38739 11.3901 8.38899 11.3938 8.39184 11.3966C8.39468 11.3993 8.39855 11.4009 8.40257 11.4009H8.41223C8.62819 11.3998 8.83488 11.319 8.98682 11.1762C9.13877 11.0334 9.22353 10.8404 9.22247 10.6395V10.6302C9.22195 10.5307 9.20036 10.4323 9.15894 10.3406C9.11753 10.2489 9.05709 10.1657 8.98108 10.0958C8.90507 10.0258 8.81498 9.97045 8.71596 9.93288C8.61693 9.89531 8.51091 9.87625 8.40395 9.8768H8.39429C8.39026 9.8768 8.3864 9.87834 8.38356 9.88109C8.38071 9.88384 8.37911 9.88757 8.37911 9.89146Z"
            />
          </svg>
        </div>
        <div class="flex text-xl">{{ t('projectLaborBill.title') }}</div>
      </div>
    </div>
  </div>
  <!--  <SkeletonLaborBill v-if="loadingLaborbill" />-->
  <div v-if="loadingLaborbill">
    <div class="flex align-items-center justify-content-center flex-wrap col gap-2 h-20rem">
      <h6 class="text-center font-bold">{{ t('projectLaborBill.gatheringInformation') }}...</h6>
    </div>
  </div>
  <template v-else>
    <template v-if="currentDocument.name !== null">
      <div v-if="md" class="flex flex-row justify-content-evenly align-items-center align-content-center gap-2">
        <h3 class="line-item text-lg font-bold text-center" :class="{ twoOrMore: items.length > 1 }" style="margin: 1.5rem 0 1rem 0">
          {{ t('projectLaborBill.description') }}
        </h3>
        <h3 class="line-item text-lg font-bold text-center" :class="{ twoOrMore: items.length > 1 }">
          {{ t('projectLaborBill.laborCost') }}
        </h3>
        <h3 class="line-item text-lg font-bold text-center" :class="{ twoOrMore: items.length > 1 }">
          {{ t('projectLaborBill.quantity') }}
        </h3>
        <h3 class="line-item text-lg font-bold text-center" :class="{ twoOrMore: items.length > 1 }">
          {{ t('projectLaborBill.total') }}
        </h3>
        <div v-if="items.length > 1" style="width: 40px; height: 40px" />
      </div>
      <div v-for="(item, index) in items" :key="`itemList-${index}`" class="flex flex-column md:flex-row justify-content-start align-items-start gap-2 mb-2 w-full">
        <div
          class="flex flex-column md:flex-row justify-content-evently align-items-center gap-2"
          :style="{
            width: items.length > 1 && md ? 'calc(100% - 0.5rem - 40px)' : '100%',
          }"
        >
          <Divider v-if="!md" class="line-item-break">{{ t('projectLaborBill.lineItem') }} {{ index + 1 }}</Divider>
          <div class="flex flex-row align-items-center w-full md:w-3">
            <div class="mb-2 flex flex-row w-full align-items-center gap-2">
              <label v-if="!md" class="field-label">
                {{ t('projectLaborBill.description') }}
              </label>
              <InputText v-model="item.description" class="w-full labor-input" type="text" />
            </div>
          </div>
          <div class="flex flex-row align-items-center w-full md:w-3">
            <div class="mb-2 flex flex-row w-full align-items-center gap-2">
              <label v-if="!md" class="field-label">{{ t('projectLaborBill.laborCost') }}</label>
              <InputNumber
                v-model="item.laborCost"
                :input-props="{
                  inputmode: 'decimal',
                }"
                class="w-full labor-input"
                placeholder="0.00"
                mode="currency"
                currency="USD"
                locale="en-US"
                @input="(event) => handleLaborCostUpdate(event, index)"
              />
            </div>
          </div>
          <div class="flex flex-row align-items-center w-full md:w-3">
            <div class="mb-2 flex w-full align-items-center gap-2">
              <label v-if="!md" class="field-label">{{ t('projectLaborBill.quantity') }}</label>
              <InputNumber
                v-model="item.quantity"
                class="w-full labor-input"
                placeholder="0"
                :input-props="{
                  inputmode: 'decimal',
                }"
                @input="(event) => handleQuantityUpdate(event, index)"
              />
            </div>
          </div>
          <div class="flex flex-row align-items-center w-full md:w-3">
            <div class="mb-2 flex flex-row w-full align-items-center gap-2">
              <label v-if="!md" class="field-label">{{ t('projectLaborBill.total') }}</label>
              <InputNumber v-model="item.laborCostTotal" class="w-full labor-input" mode="currency" currency="USD" placeholder="0.00" locale="en-US" :readonly="true" />
            </div>
          </div>
        </div>
        <Button
          v-if="items.length > 1"
          class="p-button-danger"
          :class="{ 'p-button-rounded': md }"
          style="height: 40px"
          :style="{
            width: md ? '40px' : '100%',
            'min-width': md ? '40px' : '100%',
          }"
          icon="pi pi-trash"
          :label="md ? '' : `${t('projectLaborBill.deleteLineItem')} ${index + 1}`"
          @click="deleteItem(index)"
        />
      </div>
      <div class="flex flex-column md:flex-row justify-content-start align-items-center align-content-center gap-2 w-full mb-2">
        <div
          class="flex flex-column md:flex-row justify-content-evently align-items-center gap-2"
          :class="{
            'justify-content-end': !project.isAuthorized,
          }"
          :style="{
            width: items.length > 1 && md ? 'calc(100% - 0.5rem - 40px)' : '100%',
          }"
        >
          <PermissionRole v-if="project.isAuthorized" :nodo-name="'projectsLaborBillPermissions'" :key-name="'add'">
            <Button :label="t('projectLaborBill.addLineItem')" class="p-button-success w-full md:w-3" icon="pi pi-plus" @click="addItem" />
          </PermissionRole>
          <div v-if="md" class="w-full md:w-3" />
          <div v-if="md" class="w-full md:w-3" />
          <div class="flex flex-row align-items-center w-full md:w-3">
            <div class="mb-2 flex flex-row align-items-center gap-2 w-full">
              <label v-if="!md" class="field-label">{{ t('projectLaborBill.totalLaborCost') }}</label>
              <InputNumber v-model="totalLaborCost" class="w-full labor-input" mode="currency" currency="USD" locale="en-US" :readonly="true" />
            </div>
          </div>
        </div>
        <div v-if="items.length > 1 && md" style="width: 40px; height: 40px" />
      </div>
      <div class="flex flex-row justify-content-start align-items-center align-content-center gap-2 w-full">
        <div
          v-if="project.isAuthorized"
          class="flex flex-column md:flex-row justify-content-evently align-items-center gap-2"
          :style="{
            width: items.length > 1 && md ? 'calc(100% - 0.5rem - 40px)' : '100%',
          }"
        >
          <div v-if="md" class="w-full md:w-3" />
          <div v-if="md" class="w-full md:w-3" />
          <div v-if="md" class="w-full md:w-3" />
          <Button :label="t('projectLaborBill.submit')" :loading="formLoading" :disabled="formLoading" class="p-button-info w-full md:w-3" @click="submitDocument" />
        </div>
        <div v-if="items.length > 1 && md" style="width: 40px; height: 40px" />
      </div>
    </template>
    <div v-else class="flex align-items-center justify-content-center flex-wrap col gap-2 h-20rem">
      <h6 class="text-center font-bold">
        {{ t('projectLaborBill.noFound') }}
      </h6>
    </div>
  </template>
</template>

<style lang="scss" scoped>
  .title-tab {
    min-height: 40px;
  }

  .line-item {
    width: calc(100% / 4);

    &.twoOrMore {
      width: calc(100% / 4 - 1rem);
    }
  }

  .p-divider.p-divider-horizontal::before {
    border-top: 1px solid rgba(0, 0, 0, 0.3);
  }

  .field-label {
    min-width: 110px;
  }
</style>

<style lang="scss">
  .line-item-break.p-divider {
    > .p-divider-content {
      background-color: #eff3f8;
    }
  }

  .labor-input {
    > input {
      width: 100%;
    }
  }
</style>
